import {
  Button,
  Checkbox,
  DateField,
  DocumentField,
  LineSeparator,
  PhoneField,
  SelectBox,
  TextField,
  ZipCodeField
} from '@icarros/torque-react';
import { CEP, City, User, getStateCities } from '../../services/MyData.service';
import { Controller, useForm } from 'react-hook-form';
import React, { ChangeEvent, useEffect } from 'react';
import { STATE_CITIES_QUERY, ZIP_CODE_QUERY } from '../../utils/queries/userQueries';
import { UserSchema, userSchema } from './MyData.validade';
import { formatDateToGlobalPattern, onlyNumbers } from '../../utils/format';

import { AxiosError } from 'axios';
import FormSection from '../../components/FormSection';
import { Icon } from '@icarros/ids-react';
import LoadingComponent from '../../components/Loading';
import { PhoneScamNotifyDefault } from '../../rebranding/components/PhoneScamNotify';
import { brazilianStates } from '../../utils/cities';
import moment from 'moment';
import { useKeycloak } from '@react-keycloak/web';
import { useQuery } from '@tanstack/react-query';
import { useUserFetching } from './hooks/useUserFetching';
import { useWebappFlags } from '../../rebranding/hooks/useWebAppFlags';
import { zodResolver } from '@hookform/resolvers/zod';

export function UserForm({
  userData
}: Readonly<{
  userData: User;
}>): JSX.Element {
  const { keycloak } = useKeycloak();
  const dataNascimento = moment(userData.dataNascimento).format('DD/MM/YYYY');
  const { getFlagOnSession } = useWebappFlags();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setFocus,
    watch
  } = useForm<UserSchema>({
    resolver: zodResolver(userSchema),
    values: { ...userData, dataNascimento }
  });

  const observedUf = watch('ufId');
  const observedCep = watch('cep');

  const states = useQuery({
    queryFn() {
      if (!observedUf) return;
      return getStateCities(observedUf);
    },
    queryKey: [STATE_CITIES_QUERY, observedUf],
    enabled: observedUf != null,
    meta: {
      errorMessage: 'Desculpe, não foi possível buscar pela lista de cidades nesse momento'
    }
  });

  const { fetchCities, fetchZipCode, saveDataMutation } = useUserFetching({
    reset,
    userData,
    dataNascimento
  });

  const {
    data: dataCEP,
    status: statusCEP,
    error
  } = useQuery<CEP>({
    queryKey: [ZIP_CODE_QUERY, cleanZipCode(observedCep)],
    enabled: observedCep != null && observedCep.length > 8
  });

  useEffect(() => {
    if (statusCEP == 'success' && dataCEP) {
      const { cityDescription, cityId, district, streetAddress, uf } = dataCEP;
      setValue('cidadeId', cityId ?? 0);
      setValue('cidadeNome', cityDescription ?? '');
      setValue('bairro', district ?? '');
      setValue('ufId', uf ?? '');
      setValue('endereco', streetAddress ?? '');
    }
  }, [dataCEP, setValue, statusCEP]);

  useEffect(() => {
    const firstError = Object.keys(errors).find(field => errors[field as keyof typeof errors]);
    if (firstError) {
      setFocus(firstError as keyof UserSchema);
    }
  }, [errors, setFocus]);

  function cleanZipCode(zipCode: string | null) {
    if (!zipCode) return '';
    return zipCode.replace(/\D/g, '');
  }

  function testCPFSize(cpfValue: string) {
    const CPFSizeRegex = /\d{11}/;
    return !!cpfValue && CPFSizeRegex.test(cpfValue);
  }

  function goToChangePassword() {
    window.location.href = keycloak.createAccountUrl();
  }

  function getStateCitiesList(states?: City[]) {
    return states?.map(elem => ({
      label: elem.nome,
      value: elem.id
    }));
  }

  function createUserOnIdMissing(userId?: number) {
    return userId ? 'put' : 'post';
  }

  function handleProfileUpdate(data: UserSchema) {
    const result = {
      ...data,
      dataNascimento: moment(formatDateToGlobalPattern(data.dataNascimento)).valueOf(),
      cpf: onlyNumbers(data.cpf ?? ''),
      celular: onlyNumbers(data.celular ?? ''),
      optins: data.optins.filter(optin => optin.status),
      telefone: onlyNumbers(data?.telefone ?? ''),
      cep: onlyNumbers(data.cep ?? ''),
      ufNome: brazilianStates.find(item => item.state == data.ufId)?.stateName ?? '',
      cidadeNome: states.data?.find(item => item.id == data.cidadeId)?.nome ?? '',
      id: userData.id
    };

    saveDataMutation.mutate({
      data: result,
      type: createUserOnIdMissing(result.id)
    });
  }

  function isAddressInputFormDisabled(name: keyof UserSchema, cepType: keyof CEP) {
    const possibleAxiosError = error as AxiosError;
    const isCEPFetchError = possibleAxiosError?.isAxiosError;

    if (isCEPFetchError) return false;
    if (statusCEP == 'success' && dataCEP[cepType] == null) return false;
    if (userData[name]) return true;
    return false;
  }

  function handleCEPChange(content: string) {
    const clearZipCode = cleanZipCode(content);
    if (clearZipCode.length === 8) {
      fetchZipCode(clearZipCode);
    }
  }

  return (
    <div className="my-data main-content main-content--has-disclaimer">
      {saveDataMutation.isLoading && <LoadingComponent />}
      <span className="my-data__main-title">Meus Dados</span>
      <form onSubmit={handleSubmit(handleProfileUpdate)}>
        <FormSection title="Dados Pessoais" className="my-data-form-section">
          <div className="my-data-form-section__fields">
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  innerRef={field.ref}
                  id="txt_qa_nome"
                  idError="txt_qa_nome-error"
                  name="name"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="Nome Completo"
                  placeholder="Digite seu nome"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  innerRef={field.ref}
                  id="txt_qa_email"
                  idError="txt_qa_email-error"
                  name="email"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="E-mail"
                  placeholder="exemplo@email.com"
                  disabled
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <DocumentField
                  {...field}
                  id="txt_qa_cpf"
                  idError="txt_qa_cpf-error"
                  className="my-data-form-section__field my-data-form-section__field--small"
                  label="CPF"
                  placeholder="000.000.000-00"
                  disabled={testCPFSize(userData.cpf ?? '')}
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="dataNascimento"
              control={control}
              render={({ field }) => (
                <DateField
                  {...field}
                  id="txt_qa_nascimento"
                  idError="txt_qa_nascimento-error"
                  className="my-data-form-section__field my-data-form-section__field--small"
                  label="Data de Nascimento"
                  placeholder="__/__/____"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </div>
        </FormSection>
        <LineSeparator />

        <FormSection title="Telefone" className="my-data-form-section">
          <div className="my-data-form-section__fields">
            <Controller
              name="celular"
              control={control}
              render={({ field }) => (
                <PhoneField
                  {...field}
                  id="txt_qa_celular"
                  idError="txt_qa_celular-error"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="Celular"
                  placeholder="(99) 99999-9999"
                  tooltipText="Mantenha seu número atualizado para receber propostas no celular"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <PhoneField
                  {...field}
                  id="txt_qa_telefone"
                  idError="txt_qa_telefone-error"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="Residencial (Opcional)"
                  placeholder="(66) 6666-6666"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            {getFlagOnSession('whatsappBot') && (
              <div className="whatsapp-alert">
                <Icon name="itaufonts_whatsapp" variant="secondary" size="medium" />
                <span>Enviaremos propostas de pessoas interessadas via WhatsApp </span>
              </div>
            )}
            {!getFlagOnSession('whatsappBot') && (
              <Controller
                name="mostrarTelefone"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="showPhone"
                    innerRef={field.ref}
                    defaultChecked={field.value}
                    className="my-data-form-section__field my-data-form-section__field--checkbox"
                  >
                    Ativar a exibição do meu número no anúncio, desta forma, possíveis interessados
                    podem entrar em contato comigo por telefone.
                  </Checkbox>
                )}
              />
            )}
          </div>
        </FormSection>

        <PhoneScamNotifyDefault />
        <LineSeparator />

        <FormSection title="Endereço" className="my-data-form-section">
          <div className="my-data-form-section__fields">
            <Controller
              name="cep"
              control={control}
              render={({ field }) => (
                <ZipCodeField
                  {...field}
                  id="txt_qa_cep"
                  idError="txt_qa_cep-error"
                  name="zipCode"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="CEP"
                  placeholder="00000-000"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const eventZipCode = e.target.value;
                    handleCEPChange(eventZipCode);

                    return field.onChange(e);
                  }}
                  onIconClick={() => {
                    const eventZipCode = field.value;
                    handleCEPChange(eventZipCode ?? '');
                  }}
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="endereco"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  innerRef={field.ref}
                  id="txt_qa_endereco"
                  idError="txt_qa_endereco-error"
                  name="address"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="Endereço"
                  placeholder="Rua, avenida, alameda..."
                  disabled={isAddressInputFormDisabled('endereco', 'streetAddress')}
                />
              )}
            />
            <Controller
              name="bairro"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  innerRef={field.ref}
                  id="txt_qa_bairro"
                  idError="txt_qa_bairro-error"
                  name="district"
                  className="my-data-form-section__field my-data-form-section__field--normal"
                  label="Bairro"
                  placeholder="Seu bairro"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                  disabled={isAddressInputFormDisabled('bairro', 'district')}
                />
              )}
            />
            <Controller
              name="ufId"
              control={control}
              render={({ field }) => (
                <SelectBox
                  {...field}
                  innerRef={field.ref}
                  id="cmb_qa_estado"
                  idError="cmb_qa_estado-error"
                  list={brazilianStates.map(elem => ({
                    label: elem.stateName,
                    value: elem.state
                  }))}
                  disabled={isAddressInputFormDisabled('ufId', 'uf')}
                  name="state"
                  className="my-data-form-section__field my-data-form-section__field--small"
                  label="Estado"
                  placeholder="Selecione"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    fetchCities(e.target.value);
                    setValue('cidadeNome', '');
                    setValue('cidadeId', 0);
                    return field.onChange(e);
                  }}
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
            <Controller
              name="cidadeId"
              control={control}
              render={({ field }) => (
                <SelectBox
                  {...field}
                  innerRef={field.ref}
                  id="cmb_qa_cidade"
                  idError="cmb_qa_cidade-error"
                  list={getStateCitiesList(states.data)}
                  disabled={isAddressInputFormDisabled('cidadeId', 'cityId')}
                  name="city"
                  className="my-data-form-section__field my-data-form-section__field--small"
                  label="Cidade"
                  placeholder="Selecione"
                  helper={errors[field.name]?.message}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </div>
        </FormSection>
        <LineSeparator />
        <FormSection title="Senha" className="my-data-form-section">
          <div className="single-line">
            <span className="single-line__title">
              Sua senha é importante para a segurança de sua conta aqui no iCarros e pode ser
              alterada a qualquer momento.
            </span>
            <Button
              id="btn_qa_alterarsenha"
              className="single-line__button"
              secondary
              colorSecondary
              small
              onClick={goToChangePassword}
            >
              Alterar senha
            </Button>
          </div>
        </FormSection>
        {userData.optins.length > 0 && (
          <>
            <LineSeparator />
            <FormSection title="Configurações de Notificação" className="my-data-form-section">
              <div className="my-data-form-section__fields">
                {userData.optins.map((elem, index) => (
                  <div
                    className="my-data-check-field"
                    id={`cbx-optin-${elem.id}`}
                    key={`checkbox-${elem.id}`}
                  >
                    <Controller
                      name={`optins.${index}.status`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          innerRef={field.ref}
                          className="my-data-check-field__checkbox"
                          defaultChecked={field.value}
                        >
                          {elem.nome}
                        </Checkbox>
                      )}
                    />
                    <span className="my-data-check-field__label">{elem.descricao}</span>
                  </div>
                ))}
              </div>
            </FormSection>
          </>
        )}
        <Button id="btn_qa_salvar" className="my-data__save" primary type="submit">
          Salvar
        </Button>
      </form>
    </div>
  );
}
